import { MultiselectButtonDefinition } from '../../models/multiselect-button.model';
import { Component, HostBinding, HostListener, Input, OnChanges, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MenuAction } from '@anecdotes/design-system/dropdown-menu';
import { MultiselectLoader, MultiselectLoaderType } from '../../constants';

@Component({
  selector: 'app-multiselect-host',
  templateUrl: './multiselect-host.component.html',
  styleUrls: ['./multiselect-host.component.scss'],
  standalone: false,
})
export class MultiselectHostComponent implements OnChanges {
  readonly selectedItemsMap: Map<string, any> = new Map();

  readonly itemsSelection$ = new BehaviorSubject<Map<string, any>>(this.selectedItemsMap);

  selectAllCheckmark: boolean;

  isLoadingState$ = new BehaviorSubject<boolean>(false);

  selectedMapping: { [key: string]: string } = {
    '=1': this.buildTranslationKey('selectedCount.singular'),
    other: this.buildTranslationKey('selectedCount.plural'),
  };

  @Input()
  items: any[];

  @Input()
  actionButtons: MultiselectButtonDefinition[];

  @Input()
  threeDotsMenu: MenuAction<any>[];

  @Input()
  menuText: string;

  @Input()
  selectDefinition: (item: any) => any;

  @Input()
  alwaysShowMultiselect = false;

  @Input()
  alwaysDeselectAllAfterAction = true;

  @Input()
  shouldDisableAdding: boolean;

  @Input()
  loaderType: MultiselectLoaderType = MultiselectLoader.circle;

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  onItemSelected = new EventEmitter();

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  onItemUnselected = new EventEmitter();

  @HostBinding('class.visible')
  get isMultiselectVisible(): boolean {
    return this.alwaysShowMultiselect || !!this.selectedItemsMap?.size;
  }

  get areAllSelected(): boolean {
    return this.items?.length === this.selectedItemsMap.size;
  }

  get isNoneSelected(): boolean {
    return this.selectedItemsMap.size === 0;
  }

  get isCircleLoader(): boolean {
    return this.loaderType === MultiselectLoader.circle;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('threeDotsMenu' in changes) {
      this.wrapMultiselectAction(this.threeDotsMenu);
    }

    if ('actionButtons' in changes) {
      this.wrapMultiselectAction(this.actionButtons);
    }
  }

  buildTranslationKey(relativeKey: string): string {
    return `multiselect.${relativeKey}`;
  }

  selectAll(checkmarkData: boolean | null): void {
    if (checkmarkData) {
      this.selectedItemsMap.clear();
      this.items.forEach((element) => {
        this.selectedItemsMap.set(this.selectDefinition(element), element);
      });
      this.itemsSelection$.next(this.selectedItemsMap);

      // Force reassign checkmark value in case if this method esed externally, not as a valueChnage handler for ckeckbox
      this.selectAllCheckmark = this.areAllSelected;
    } else if (checkmarkData === false) {
      this.selectedItemsMap.clear();
      this.itemsSelection$.next(this.selectedItemsMap);

      // Force reassign checkmark value
      this.selectAllCheckmark = this.areAllSelected;
    }
  }

  selectItem(item: any): void {
    const previousValue = JSON.stringify([...this.selectedItemsMap.keys()]);
    this.selectedItemsMap.set(this.selectDefinition(item), item);
    const newValue = JSON.stringify([...this.selectedItemsMap.keys()]);

    this.selectAllCheckmark = this.areAllSelected;
    this.itemsSelection$.next(this.selectedItemsMap);

    const areItemsChanged = previousValue !== newValue;

    if (areItemsChanged) {
      this.onItemSelected.emit(item);
    }
  }

  unselectItem(item: any): void {
    const previousValue = JSON.stringify([...this.selectedItemsMap.keys()]);
    this.selectedItemsMap.delete(this.selectDefinition(item));
    const newValue = JSON.stringify([...this.selectedItemsMap.keys()]);

    this.selectAllCheckmark = this.areAllSelected;
    this.itemsSelection$.next(this.selectedItemsMap);

    const areItemsChanged = previousValue !== newValue;

    if (areItemsChanged) {
      this.onItemUnselected.emit(item);
    }
  }

  clearSelection(): void {
    this.selectedItemsMap.clear();
    this.selectAll(false);
  }

  getSelectedItemsIds(): string[] { return [ ...this.selectedItemsMap.keys() ]; }

  updateLoadingState(state: boolean): void {
    this.isLoadingState$.next(state);
  }

  private wrapMultiselectAction(objects: { action?: (args: any) => Promise<any> | any, openableButton?: boolean }[]): void {
    objects?.forEach((objWithAction, index) => {
      if (objects[index].action && !objects[index].openableButton) {
        objects[index].action = this.createActionWithMultiselectObject.bind(this, objWithAction.action.bind(this));
      }
    });
  }

  private async createActionWithMultiselectObject(action: (...args: any) => Promise<any> | any): Promise<void> {
    this.isLoadingState$.next(true);
    await action(this.selectedItemsMap);

    if (this.alwaysDeselectAllAfterAction) {
      this.selectAll(false);
    }

    this.isLoadingState$.next(false);
  }

  @HostListener('document:keydown.escape', ['$event'])
  private onKeydownHandler(): void {
    this.selectAll(false);
  }
}
